import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../context/AdminContext';
import RichEditor from './RichEditor';
import './AdminContent.css';
import { IMAGE_BASE_URL } from '../constant';

const emptyForm = { title: '', description: '', farming_steps: '' };

// ─── Add Language Modal ───────────────────────────────────────────────────────
function AddLanguageModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', code: '', native_name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'name') {
        updated.code = value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '').slice(0, 5);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) { setError('Name and code are required'); return; }
    setLoading(true);
    setError('');
    try {
      await adminApi.post('/admin/languages', form);
      onAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add language');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🌐 Add New Language</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="alert alert-error" style={{ margin: '0 0 16px' }}>{error}</div>}
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Language Name *</label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Marathi" required />
          </div>
          <div className="form-group">
            <label>Language Code * <span style={{ color: '#94a3b8', fontWeight: 400 }}>(2–5 letters, e.g. mr)</span></label>
            <input name="code" value={form.code} onChange={handleChange} placeholder="e.g. mr" maxLength={5} required />
          </div>
          <div className="form-group">
            <label>Native Name <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
            <input name="native_name" value={form.native_name} onChange={handleChange} placeholder="e.g. मराठी" />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '⏳ Adding...' : '✅ Add Language'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminContent() {
  const [subProducts, setSubProducts] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedSP, setSelectedSP] = useState(null);   // full object
  const [selectedLang, setSelectedLang] = useState(null); // full object
  const [form, setForm] = useState(emptyForm);
  const [contentId, setContentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showLangModal, setShowLangModal] = useState(false);
  const [step, setStep] = useState(1); // 1=select SP, 2=select lang, 3=edit

  const showAlert = (msg, type = 'success') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 4000);
  };

  // Load sub-products and languages
  const loadInitialData = useCallback(() => {
    Promise.all([
      adminApi.get('/admin/sub-products'),
      adminApi.get('/admin/languages'),
    ]).then(([spRes, langRes]) => {
      setSubProducts(spRes.data);
      setLanguages(langRes.data);
    }).catch(() => showAlert('Failed to load data', 'error'));
  }, []);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);

  // Load content when SP + lang both selected
  const loadContent = useCallback(() => {
    if (!selectedSP || !selectedLang) return;
    setFetching(true);
    adminApi.get(`/admin/content/${selectedSP.id}/${selectedLang.code}`)
      .then(res => {
        if (res.data) {
          setForm({
            title: res.data.title || '',
            description: res.data.description || '',
            farming_steps: res.data.farming_steps || '',
          });
          setContentId(res.data.id);
        } else {
          setForm(emptyForm);
          setContentId(null);
        }
      })
      .catch(() => { setForm(emptyForm); setContentId(null); })
      .finally(() => setFetching(false));
  }, [selectedSP, selectedLang]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const handleSelectSP = (sp) => {
    setSelectedSP(sp);
    setSelectedLang(null);
    setForm(emptyForm);
    setContentId(null);
    setStep(2);
  };

  const handleSelectLang = (lang) => {
    setSelectedLang(lang);
    setStep(3);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedSP || !selectedLang) return showAlert('Select sub product and language first', 'error');
    if (!form.title.trim()) return showAlert('Title is required', 'error');
    setSaving(true);
    try {
      if (contentId) {
        await adminApi.put(`/admin/content/${contentId}`, form);
        showAlert('✅ Content updated successfully!');
      } else {
        const res = await adminApi.post('/admin/content', {
          sub_product_id: selectedSP.id,
          language_code: selectedLang.code,
          ...form,
        });
        setContentId(res.data.id);
        showAlert('✅ Content saved successfully!');
      }
      loadContent();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLangAdded = () => {
    loadInitialData();
    showAlert('Language added successfully!');
  };

  const handleDeleteLang = async (code, name) => {
    if (!window.confirm(`Delete language "${name}"? All content in this language will remain but the language tab will be removed.`)) return;
    try {
      await adminApi.delete(`/admin/languages/${code}`);
      showAlert('Language removed.');
      loadInitialData();
      if (selectedLang?.code === code) { setSelectedLang(null); setStep(2); }
    } catch (err) {
      showAlert(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const resetToStep1 = () => { setSelectedSP(null); setSelectedLang(null); setForm(emptyForm); setContentId(null); setStep(1); };
  const resetToStep2 = () => { setSelectedLang(null); setForm(emptyForm); setContentId(null); setStep(2); };

  return (
    <div className="ac-page">
      {/* ── Clean Header (no green banner) ── */}
      <div className="ac-header">
        <div>
          <h1 className="ac-title">Manage Product Content</h1>
          <p className="ac-subtitle">Add and manage product details, sub-products, and languages</p>
        </div>
        <button className="btn-primary" onClick={() => setShowLangModal(true)}>
          🌐 Add Language
        </button>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

      {/* ── Language Manager ── */}
      <div className="admin-card ac-lang-card">
        <div className="ac-lang-header">
          <span className="admin-card-title">🌍 Languages</span>
          <span className="ac-lang-count">{languages.length} languages</span>
        </div>
        <div className="ac-lang-list">
          {languages.map(lang => (
            <div
              key={lang.code}
              className={`ac-lang-chip ${selectedLang?.code === lang.code ? 'active' : ''} ${selectedSP ? 'clickable' : ''}`}
              onClick={() => {
                if (!selectedSP) {
                  // No sub-product selected yet — go to step 1 first
                  showAlert('Please select a sub product first', 'error');
                  return;
                }
                // Switch language and load its content immediately
                setSelectedLang(lang);
                setForm(emptyForm);
                setContentId(null);
                setStep(3);
              }}
              title={selectedSP ? `Switch to ${lang.name}` : 'Select a sub product first'}
            >
              <span>{lang.name}</span>
              <span className="ac-lang-code">{lang.code}</span>
              {!['en', 'hi', 'gu'].includes(lang.code) && (
                <button
                  className="ac-lang-del"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent chip click
                    handleDeleteLang(lang.code, lang.name);
                  }}
                  title="Remove language"
                >✕</button>
              )}
            </div>
          ))}
          <button className="ac-lang-add-btn" onClick={() => setShowLangModal(true)}>
            + Add Language
          </button>
        </div>
        {/* Hint when no sub-product selected */}
        {!selectedSP && (
          <p className="ac-lang-hint">👆 Select a sub product first, then click a language to edit its content</p>
        )}
        {/* Active context indicator */}
        {selectedSP && selectedLang && (
          <div className="ac-lang-context">
            Editing: <strong>{selectedSP.name}</strong> → <strong>{selectedLang.name}</strong>
            <button className="ac-lang-context-clear" onClick={resetToStep1}>Change</button>
          </div>
        )}
        {selectedSP && !selectedLang && (
          <div className="ac-lang-context">
            Sub product selected: <strong>{selectedSP.name}</strong> — now click a language above
          </div>
        )}
      </div>

      {/* ── Step Indicator ── */}
      <div className="ac-steps">
        {[
          { n: 1, label: 'Select Sub Product' },
          { n: 2, label: 'Select Language' },
          { n: 3, label: 'Edit Content' },
        ].map(s => (
          <div key={s.n} className={`ac-step ${step >= s.n ? 'done' : ''} ${step === s.n ? 'current' : ''}`}>
            <div className="ac-step-num">{step > s.n ? '✓' : s.n}</div>
            <div className="ac-step-label">{s.label}</div>
            {s.n < 3 && <div className="ac-step-line" />}
          </div>
        ))}
      </div>

      {/* ── Step 1: Select Sub Product ── */}
      {step === 1 && (
        <div className="admin-card">
          <div className="admin-card-title" style={{ marginBottom: 20 }}>Step 1 — Select a Sub Product</div>
          {subProducts.length === 0 ? (
            <div className="ac-empty">No sub products found. Add sub products first.</div>
          ) : (
            <div className="ac-sp-grid">
              {subProducts.map(sp => (
                <div key={sp.id} className="ac-sp-card" onClick={() => handleSelectSP(sp)}>
                  {sp.image_url
                    ? <img src={`${IMAGE_BASE_URL}${sp.image_url}`} alt={sp.name} className="ac-sp-img" />
                    : <div className="ac-sp-icon">🌿</div>
                  }
                  <div className="ac-sp-name">{sp.name}</div>
                  <div className="ac-sp-product">{sp.product_slug}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: Select Language ── */}
      {step === 2 && selectedSP && (
        <div className="admin-card">
          <div className="ac-step-header">
            <div className="admin-card-title">Step 2 — Select Language for "{selectedSP.name}"</div>
            <button className="ac-back-btn" onClick={resetToStep1}>← Change Sub Product</button>
          </div>
          <div className="ac-lang-select-grid">
            {languages.map(lang => (
              <div key={lang.code} className="ac-lang-select-card" onClick={() => handleSelectLang(lang)}>
                <div className="ac-lang-select-code">{lang.code.toUpperCase()}</div>
                <div className="ac-lang-select-name">{lang.name}</div>
                {lang.native_name && lang.native_name !== lang.name && (
                  <div className="ac-lang-select-native">{lang.native_name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 3: Edit Content ── */}
      {step === 3 && selectedSP && selectedLang && (
        <div className="admin-card">
          <div className="ac-step-header">
            <div>
              <div className="admin-card-title">
                Step 3 — {contentId ? 'Edit' : 'Add'} Content
              </div>
              <div className="ac-context-info">
                <span className="badge badge-green">{selectedSP.name}</span>
                <span className="badge badge-purple">{selectedLang.name} ({selectedLang.code})</span>
                {contentId
                  ? <span className="badge badge-blue">Existing Content</span>
                  : <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>New Content</span>
                }
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ac-back-btn" onClick={resetToStep2}>← Change Language</button>
              <button className="ac-back-btn" onClick={resetToStep1}>← Change Sub Product</button>
            </div>
          </div>

          {fetching ? (
            <div className="ac-loading">⏳ Loading content...</div>
          ) : (
            <form onSubmit={handleSave} className="admin-form">
              {/* Title */}
              <div className="form-group">
                <label>Title *</label>
                <input
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder={`Enter title in ${selectedLang.name}`}
                  required
                />
              </div>

              {/* Description — Rich Editor */}
              <div className="form-group">
                <label>Description</label>
                <RichEditor
                  value={form.description}
                  onChange={val => setForm(p => ({ ...p, description: val }))}
                  placeholder={`Enter description in ${selectedLang.name}...`}
                />
              </div>

              {/* Farming Steps — Rich Editor */}
              <div className="form-group">
                <label>
                  Farming Steps
                  <span className="ac-hint"> — bold, headings, lists, colors, paste from Google Docs</span>
                </label>
                <RichEditor
                  value={form.farming_steps}
                  onChange={val => setForm(p => ({ ...p, farming_steps: val }))}
                  placeholder={`Enter farming steps in ${selectedLang.name}...`}
                />

                {/* Live Preview */}
                {form.farming_steps && form.farming_steps !== '' && (
                  <div className="ac-preview">
                    <div className="ac-preview-label">👁️ Live Preview</div>
                    <div
                      className="ac-preview-body"
                      dangerouslySetInnerHTML={{ __html: form.farming_steps }}
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? '⏳ Saving...' : contentId ? '✅ Update Content' : '💾 Save Content'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setForm(emptyForm); setContentId(null); }}
                >
                  Clear
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── Language Modal ── */}
      {showLangModal && (
        <AddLanguageModal
          onClose={() => setShowLangModal(false)}
          onAdded={handleLangAdded}
        />
      )}
    </div>
  );
}
